import styled from "styled-components";

interface CalendarContainerProps {
  isCurrentMonth: boolean;
  isAtStartLimit: boolean;
  isAtEndLimit: boolean;
  isChangeMonthDisabled: boolean;
  isFiltered: boolean;
}

export const CalendarContainer = styled.div<CalendarContainerProps>`
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 62%;

  .fc {
    height: 100%;
    font-size: 0.9rem;
  }

  .fc .fc-toolbar {
    margin-bottom: 0.5rem;
    padding: 0 0 0.2rem 0;
  }

  .fc .fc-toolbar-title {
    font-size: 1.1rem;
  }

  .fc-toolbar-title::first-letter {
    text-transform: capitalize;
  }

  .fc-toolbar-title {
    color: var(--color-contrast-brand);
  }

  .fc .fc-button {
    padding: 0.3rem 0.4rem;
    font-size: 0.85rem;
  }

  .fc .fc-button-primary:focus {
    box-shadow: none;
  }

  .fc .fc-daygrid-day {
    min-height: 32px !important;
    max-height: 40px !important;
  }

  .fc .fc-day-disabled {
    cursor: not-allowed;
  }

  .fc .fc-day-other, .fc .fc-day-disabled {
    background-color: var(--color-opacity-disabled) !important;
  }

  .fc .fc-col-header-cell {
    padding: 0.3rem;
    font-size: 0.65rem;
    text-align: center;
    align-content: center;
  }

  .fc .fc-daygrid-day-number {
    padding: 0.2rem;
    font-size: 0.85rem;
  }

  .fc .fc-daygrid-day-frame {
    min-height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 0;
    height: 100%;
  }

  .fc .fc-daygrid-day:has(.blocked-day) {
    background-color: #FF4646A3;
  }

  .fc .fc-daygrid-day-top {
    height: -webkit-fill-available;
  }

  .fc-theme-standard td,
  .fc-theme-standard th {
    border: ${(props) => (props.isFiltered ? "2px solid var(--color-brand-300)" : "2px solid var(--color-neutral-300)")};
    ${(props) => (!props.isFiltered ? "color: var(--color-neutral-300) !important" : "")}
  }

  .fc-theme-standard th {
    text-transform: uppercase;
    border-bottom: none;
  }

  .fc .fc-button-primary {
    background-color: transparent;
    border-color: ${(props) => (props.isFiltered ? "var(--color-brand-300)" : "var(--color-neutral-300)")};
    color: ${(props) => (props.isFiltered ? "var(--color-brand-300)" : "var(--color-neutral-300)")};
  }

  .fc-customEditMultipleDates-button {
    background-color: ${(props) => (props.isFiltered ? "var(--color-brand-300)" : "var(--color-neutral-300)")} !important;
    border-color: ${(props) => (props.isFiltered ? "var(--color-brand-300)" : "var(--color-neutral-300)")} !important;
    color: var(--color-contrast-white) !important;
  }

  .fc .fc-button-primary:hover {
    background-color: var(--color-brand-300);
    border-color: var(--color-brand-300);
    color: var(--color-contrast-white);
  }

  .fc-customEditMultipleDates-button:hover {
    background-color: transparent !important;
    color: var(--color-brand-300) !important;
  }

  button:disabled:hover {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .fc-customEditMultipleDates-button {
    ${(props) =>
      !props.isFiltered
        ? `
      opacity: 0.5;
      cursor: not-allowed !important;
      pointer-events: none;
      `
        : ""}
  }

  /* Estilo para o botão Today quando o mês atual está sendo exibido */
  .fc-customToday-button {
    ${(props) =>
      props.isCurrentMonth || props.isChangeMonthDisabled || !props.isFiltered
        ? `
      opacity: 0.5;
      cursor: not-allowed !important;
      pointer-events: none;
    `
        : ""}
  }

  /* Estilos para os botões Prev e PrevYear quando no limite inicial */
  .fc-customPrev-button,
  .fc-customPrevYear-button {
    ${(props) =>
      props.isAtStartLimit || props.isChangeMonthDisabled || !props.isFiltered
        ? `
      opacity: 0.5;
      cursor: not-allowed !important;
      pointer-events: none;
    `
        : ""}
  }

  /* Estilos para os botões Next e NextYear quando no limite final */
  .fc-customNext-button,
  .fc-customNextYear-button {
    ${(props) =>
      props.isAtEndLimit || props.isChangeMonthDisabled || !props.isFiltered
        ? `
      opacity: 0.5;
      cursor: not-allowed !important;
      pointer-events: none;
    `
        : ""}
  }
`;

interface DayCellCustomProps {
  isPastDate: boolean;
  isOtherMonth: boolean;
  isFiltered: boolean;
  isBlocked: boolean;
}

const getDayNumberColor = (props: DayCellCustomProps) => {
  if (props.isBlocked) return "#AE1E1E";
  if (props.isFiltered) return "var(--color-brand-300)";
  return "var(--color-neutral-300)";
};

export const DayCellCustom = styled.label<DayCellCustomProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 0.2rem;
  border: none;
  background-color: transparent;
  cursor: ${(props) => (props.isPastDate || props.isOtherMonth ? "not-allowed" : "pointer")};

  .day-number {
    position: absolute;
    top: 0.1rem;
    right: 0.1rem;
    font-size: 0.75rem;
    color: ${getDayNumberColor};
    font-weight: 500;
    z-index: 1;
    opacity: ${(props) => (props.isPastDate && !props.isOtherMonth ? 0.6 : 0.85)};
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    flex: 1;
  }

  .input-day-cell {
    width: 60%;
    background-color: transparent !important;
    border: none;
    outline: none;
    text-align: center;
    padding: 0.2rem 0;
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1;
    color: ${(props) => (props.isFiltered ? "var(--color-brand-300)" : "var(--color-neutral-300)")};
  }

  input.input-day-cell:disabled {
    pointer-events: none; /* Já existe implicitamente, mas vamos garantir */
    ${(props) => (props.isPastDate && !props.isOtherMonth ? `opacity: 0.6;` : "")}
  }

  @media screen and (max-width: 1400px) {
    .input-day-cell {
      font-size: 1rem;
    }
  }
`;